/**
 * Evidence Calculation Tab
 *
 * Shown to LENDER and AUDITOR disclosure levels.
 * - LENDER: sees formula, steps, config_ref (no weights/thresholds)
 * - AUDITOR: sees everything including weights and thresholds
 *
 * ⚠️ Weights and thresholds are proprietary - AUDITOR only
 *
 * EPIC 63 US63.1: Removed duplicate formula blocks, added mapping table
 * EPIC 63 US63.2: Added calculation context block
 */

import React from 'react';
import { Code, GitBranch, Lock, CheckCircle, Info } from 'lucide-react';
import { EvidenceContract } from '@/types/evidenceContract';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';

/** Run metadata for Analysis Window fallback (VeraScore) */
interface EvidenceRunMetadata {
  coverage_months_available?: number;
  coverage_months_used?: number;
  data_freshness_ts?: string;
  input_months_coverage?: number;
}

interface EvidenceCalculationTabProps {
  evidence: EvidenceContract | null;
  /** Run metadata for Analysis Window fallback (VeraScore composite metric) */
  runMetadata?: EvidenceRunMetadata | null;
}

/** EPIC 62 Phase 2B: FactorAuditDTO mapping structure */
interface FactorAuditDTOMapping {
  mapping?: {
    curve_formula: string;
    computed_score: number;
    curve_points?: Array<{ x: number; y: number }>;
  };
  algorithm_metadata?: {
    version: string;
    git_sha: string;
    ruleset_version: string;
  };
  // BUG-UI-097 FIX: Backend sends 'start'/'end', not 'start_date'/'end_date'
  time_window?: {
    start?: string;      // Backend sends this (ISO 8601)
    end?: string;        // Backend sends this (ISO 8601)
    start_date?: string; // Legacy/alternative field name
    end_date?: string;   // Legacy/alternative field name
    months_covered?: number;
  };
  // BUG-UI-098 FIX: quality_rollup contains coverage_months
  quality_rollup?: {
    accounts_used: number;
    txns_used: number;
    bureau_tradelines_used: number;
    coverage_months: number;
    freshness_days: number;
  };
}

export function EvidenceCalculationTab({ evidence, runMetadata }: EvidenceCalculationTabProps) {
  // EPIC 62 Phase 2B: Extract factor_audit_dto for enhanced calculation display
  const factorAuditDTO = evidence?.intermediate_metrics?.factor_audit_dto as FactorAuditDTOMapping | undefined;

  // Check if this is VeraScore (composite metric without factor_audit_dto)
  const isVeraScore = evidence?.metric_key?.toLowerCase().includes('verascore');

  // Check if this is an income metric (gross_income_6m, deposited_income, etc.)
  const isIncomeMetric = evidence?.metric_key?.includes('gross_income') ||
                         evidence?.metric_key?.includes('income_6m') ||
                         evidence?.metric_key?.includes('deposited_income');

  // FIX Jan 22, 2026: Check if this is a Bottom 10 KPI metric that needs Analysis Window
  const metricKey = evidence?.metric_key?.toLowerCase() || '';
  const isBottom10KPIMetric = [
    'money_in_1m',
    'money_out_1m',
    'cash_on_hand',
    'monthly_debt_payments',
    'total_debt',
    'money_leftover_1m',
    'net_savings_1m',
    'dti_ratio'
  ].includes(metricKey);

  // BUG-UI-007: Show helpful message when calculation data not yet available
  // EPIC 62 Phase 2B: Can still show factor_audit_dto mapping if available
  // FIX: VeraScore always has calculation data, so don't show empty state for it
  if (!evidence?.calculation && !factorAuditDTO?.mapping && !isVeraScore) {
    return (
      <div className="text-center text-muted-foreground py-8">
        <Code className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="font-medium">Calculation Details</p>
        <p className="text-sm mt-2">
          Formula and calculation steps will be available once the evidence API
          is fully populated for this metric.
        </p>
      </div>
    );
  }

  const { calculation, integrity } = evidence || {};
  const { formula, steps, config_ref, weights, thresholds } = calculation || {};

  // EPIC 62 Phase 2B: Prefer factor_audit_dto algorithm_metadata when available
  const effectiveConfigRef = factorAuditDTO?.algorithm_metadata || config_ref;

  // BUG-UI-030: Check if we have formula/steps for Math Check section
  // EPIC 62 Phase 2B: Also check factor_audit_dto.mapping for formula
  const hasFormula = !!formula || !!factorAuditDTO?.mapping?.curve_formula;
  const hasSteps = steps && steps.length > 0;
  const effectiveFormula = factorAuditDTO?.mapping?.curve_formula || formula;

  // Determine if we should show Analysis Window (either from factor_audit_dto or runMetadata for VeraScore)
  const hasFactorTimeWindow = !!factorAuditDTO?.time_window;
  const hasRunMetadataCoverage = isVeraScore && runMetadata?.coverage_months_used;

  return (
    <div className="space-y-6">
      {/* EPIC 62 Phase 2B: Time Window from factor_audit_dto */}
      {hasFactorTimeWindow && (
        <div className="border border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Analysis Window
            </h3>
          </div>
          <div className="bg-slate-800 p-4 space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Period:</span>
              <span className="text-white">
                {/* BUG-UI-097 FIX: Use start/end (backend) or start_date/end_date (legacy) */}
                {formatDateSafe(factorAuditDTO!.time_window!.start || factorAuditDTO!.time_window!.start_date)} – {formatDateSafe(factorAuditDTO!.time_window!.end || factorAuditDTO!.time_window!.end_date)}
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Coverage:</span>
              <span className="text-white">
                {/* BUG-UI-098 FIX: Use months_covered or fallback to quality_rollup.coverage_months */}
                {factorAuditDTO!.time_window!.months_covered ?? factorAuditDTO!.quality_rollup?.coverage_months ?? 'N/A'} months
              </span>
            </div>
          </div>
        </div>
      )}

      {/* FIX: Analysis Window fallback for VeraScore using runMetadata */}
      {!hasFactorTimeWindow && hasRunMetadataCoverage && (
        <div className="border border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Analysis Window
            </h3>
          </div>
          <div className="bg-slate-800 p-4 space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Data Coverage:</span>
              <span className="text-white">
                {runMetadata?.coverage_months_used ?? runMetadata?.input_months_coverage ?? 'N/A'} months analyzed
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Available Data:</span>
              <span className="text-white">
                {runMetadata?.coverage_months_available ?? 'N/A'} months available
              </span>
            </div>
            {runMetadata?.data_freshness_ts && (
              <div className="flex gap-2">
                <span className="text-slate-400 min-w-[140px]">Data Freshness:</span>
                <span className="text-white">
                  {formatDateSafe(runMetadata.data_freshness_ts)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FIX: Analysis Window for Income metrics (gross_income_6m) */}
      {!hasFactorTimeWindow && !hasRunMetadataCoverage && isIncomeMetric && (
        <div className="border border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Analysis Window
            </h3>
          </div>
          <div className="bg-slate-800 p-4 space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Period:</span>
              <span className="text-white">
                {evidence?.period?.start && evidence?.period?.end
                  ? `${formatDateSafe(evidence.period.start)} – ${formatDateSafe(evidence.period.end)}`
                  : '6-month rolling window'}
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Coverage:</span>
              <span className="text-white">
                {(evidence?.intermediate_metrics?.month_count as number | undefined) ?? 6} months analyzed
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Data Source:</span>
              <span className="text-white">
                Aggregated deposit transactions from linked accounts
              </span>
            </div>
          </div>
        </div>
      )}

      {/* FIX Jan 22, 2026: Generic Analysis Window fallback for Bottom 10 KPI metrics */}
      {!hasFactorTimeWindow && !hasRunMetadataCoverage && !isIncomeMetric && isBottom10KPIMetric && (
        <div className="border border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Analysis Window
            </h3>
          </div>
          <div className="bg-slate-800 p-4 space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Period:</span>
              <span className="text-white">
                {evidence?.period?.start && evidence?.period?.end
                  ? `${formatDateSafe(evidence.period.start)} – ${formatDateSafe(evidence.period.end)}`
                  : 'Current month snapshot'}
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Coverage:</span>
              <span className="text-white">
                {(evidence?.intermediate_metrics?.month_count as number | undefined)
                  ?? (evidence?.intermediate_metrics?.coverage_months as number | undefined)
                  ?? runMetadata?.coverage_months_used
                  ?? 1} month{((evidence?.intermediate_metrics?.month_count as number | undefined) ?? 1) !== 1 ? 's' : ''} analyzed
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-400 min-w-[140px]">Computed Result:</span>
              <span className="text-white font-medium">
                {formatMetricValue(evidence?.value, evidence?.unit)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* EPIC 63 US63.2: Calculation Context */}
      {calculation?.calculation_context && (
        <div className="border border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Calculation Context
            </h3>
          </div>
          <div className="bg-slate-800 p-4 space-y-2 text-sm">
            {calculation.calculation_context.income_window && (
              <div className="flex gap-2">
                <span className="text-slate-400 min-w-[140px]">Income window:</span>
                <span className="text-white">{calculation.calculation_context.income_window}</span>
              </div>
            )}
            {calculation.calculation_context.debt_window && (
              <div className="flex gap-2">
                <span className="text-slate-400 min-w-[140px]">Debt window:</span>
                <span className="text-white">{calculation.calculation_context.debt_window}</span>
              </div>
            )}
            {calculation.calculation_context.income_basis && (
              <div className="flex gap-2">
                <span className="text-slate-400 min-w-[140px]">Income basis:</span>
                <span className="text-white">{calculation.calculation_context.income_basis}</span>
              </div>
            )}
            {calculation.calculation_context.debt_basis && (
              <div className="flex gap-2">
                <span className="text-slate-400 min-w-[140px]">Debt basis:</span>
                <span className="text-white">{calculation.calculation_context.debt_basis}</span>
              </div>
            )}
            {calculation.calculation_context.snapshot_id && (
              <div className="flex gap-2">
                <span className="text-slate-400 min-w-[140px]">Snapshot ID:</span>
                <code className="text-xs text-green-400 font-mono">{calculation.calculation_context.snapshot_id}</code>
              </div>
            )}
            {calculation.calculation_context.normalization_rules_version && (
              <div className="flex gap-2">
                <span className="text-slate-400 min-w-[140px]">Normalization:</span>
                <span className="text-white">{calculation.calculation_context.normalization_rules_version}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* EPIC 63 US63.1: Single "Proof" block (replaces duplicate formula sections) */}
      {hasFormula && (
        <div className="border border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-700 px-4 py-3 border-b border-slate-600 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              📐 Calculation Proof
            </h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-slate-400 hover:text-white">
                    <Info className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Rounded for display; full precision stored in database</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="bg-slate-800 p-4">
            {/* Formula - EPIC 62 Phase 2B: Use effectiveFormula */}
            <div className="font-mono text-sm text-green-400 bg-slate-900 p-3 rounded border border-slate-600 overflow-x-auto mb-4">
              {effectiveFormula}
            </div>

            {/* EPIC 62 Phase 2B: Computed Score from factor_audit_dto.mapping */}
            {factorAuditDTO?.mapping?.computed_score !== undefined && (
              <div className="flex items-center gap-2 mb-4 text-sm">
                <span className="text-slate-400">Computed Score:</span>
                <span className="font-mono text-green-400 font-semibold">
                  {factorAuditDTO.mapping.computed_score}
                </span>
              </div>
            )}

            {/* Calculation Steps - BUG-UI-096: Sort inputs first, then calculations, then output */}
            {hasSteps && (
              <div className="space-y-1 mb-4">
                {(() => {
                  // BUG-UI-096: Define step order priority (lower = shown first)
                  // Inputs first, then intermediate calculations, then final output
                  const getStepPriority = (stepName: string): number => {
                    const name = stepName.toLowerCase();
                    // Raw inputs (show first)
                    if (name.includes('sum') || name.includes('deposit') || name.includes('raw')) return 1;
                    if (name.includes('count') || name.includes('months')) return 2;
                    // Intermediate calculations
                    if (name.includes('avg') || name.includes('average') || name.includes('monthly')) return 3;
                    // Final outputs (show last)
                    if (name.includes('total') || name.includes('annual') || name.includes('final')) return 4;
                    // Default - use original order
                    return 3;
                  };

                  const sortedSteps = [...(steps || [])].sort((a, b) => {
                    const priorityA = getStepPriority(a.name);
                    const priorityB = getStepPriority(b.name);
                    if (priorityA !== priorityB) return priorityA - priorityB;
                    // If same priority, use original order field
                    return (a.order || 0) - (b.order || 0);
                  });

                  return sortedSteps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs text-slate-300">
                      <span className="text-slate-500 font-mono">{idx + 1}.</span>
                      <span className="font-medium text-slate-200">{step.name}:</span>
                      <span className="text-slate-400">{step.detail}</span>
                      {step.intermediate_value !== undefined && (
                        <span className="ml-auto font-mono text-green-400">
                          {/* BUG-UI-107: Pass metric_key to format VeraScore pts correctly */}
                          = {formatStepValue(step.name, step.intermediate_value, evidence?.metric_key)}
                        </span>
                      )}
                    </div>
                  ));
                })()}
              </div>
            )}

            {/* EPIC 63 US63.1: Mapping Table */}
            {calculation?.mapping_table && calculation.mapping_table.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-slate-300 mb-2">
                  Value → Score Mapping
                </div>
                <table className="w-full text-xs border border-slate-600 rounded overflow-hidden">
                  <thead className="bg-slate-700">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-300 border-r border-slate-600">
                        Range
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-300">
                        Score
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-slate-900">
                    {calculation.mapping_table.map((entry, idx) => (
                      <tr key={idx} className={idx > 0 ? 'border-t border-slate-700' : ''}>
                        <td className="px-3 py-2 text-white font-mono border-r border-slate-600">
                          {entry.range_label}
                        </td>
                        <td className="px-3 py-2 text-green-400 font-mono">
                          {entry.score_output}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}



      {/* Weights (THE SECRET SAUCE) */}
      {weights && Object.keys(weights).length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Factor Weights (Proprietary)
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(weights).map(([key, value]) => (
              <div 
                key={key}
                className="flex justify-between p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800"
              >
                <span className="text-sm">{key}</span>
                <span className="font-mono text-sm font-bold">{(value * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Thresholds */}
      {thresholds && Object.keys(thresholds).length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Rating Thresholds (Proprietary)
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(thresholds).map(([key, value]) => (
              <div 
                key={key}
                className="flex justify-between p-2 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-200 dark:border-purple-800"
              >
                <span className="text-sm">{key}</span>
                <span className="font-mono text-sm font-bold">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Config Reference - EPIC 62 Phase 2B: Use effectiveConfigRef */}
      {/* BUG-UI-125 FIX v2: Wrap in span for proper tooltip triggering */}
      {effectiveConfigRef && (
        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-2">
          <TooltipProvider delayDuration={100}>
            {/* Ruleset with tooltip */}
            <div className="flex items-center gap-2 text-sm">
              <Code className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Ruleset:</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help">
                    <Badge variant="outline" className="font-mono">
                      {effectiveConfigRef.ruleset_version}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs z-[100]">
                  <p className="text-xs">The scoring ruleset configuration that defines thresholds, weights, and business rules used for this calculation.</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Algorithm with tooltip */}
            <div className="flex items-center gap-2 text-sm">
              <GitBranch className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Algorithm:</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help">
                    <Badge variant="outline" className="font-mono">
                      {effectiveConfigRef.version || (config_ref as { algorithm_version?: string })?.algorithm_version || 'N/A'}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs z-[100]">
                  <p className="text-xs">The algorithm version that computed this score. Different versions may use different formulas or factor weights.</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Git SHA with tooltip */}
            <div className="flex items-center gap-2 text-sm">
              <Code className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Git SHA:</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help">
                    <code className="text-xs bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded">
                      {effectiveConfigRef.git_sha}
                    </code>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs z-[100]">
                  <p className="text-xs">The exact code version (Git commit hash) used for this calculation. Enables full audit traceability to the source code.</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
      )}

      {/* Integrity Verification */}
      {integrity && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="font-medium text-green-700 dark:text-green-300">
              Integrity Verified
            </span>
          </div>
          <div className="space-y-1 text-xs font-mono text-green-800 dark:text-green-200">
            <div>Inputs: {integrity.inputs_hash.substring(0, 16)}...</div>
            <div>Outputs: {integrity.outputs_hash.substring(0, 16)}...</div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * BUG-UI-097 FIX: Safely format a date string, returning 'N/A' if invalid
 */
function formatDateSafe(dateStr: string | undefined | null): string {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return 'N/A';
  }
}

/**
 * FIX Jan 22, 2026: Format metric value with appropriate unit for Analysis Window display
 */
function formatMetricValue(value: number | undefined | null, unit: string | undefined): string {
  if (value === undefined || value === null) return 'N/A';

  const unitLower = (unit || '').toLowerCase();

  // Currency formatting
  if (unitLower === 'usd' || unitLower === 'currency' || unitLower === '$') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  }

  // Percentage formatting
  if (unitLower === 'percent' || unitLower === '%' || unitLower === 'pct') {
    return `${value.toFixed(1)}%`;
  }

  // Count/score formatting
  if (unitLower === 'count' || unitLower === 'score' || unitLower === 'points') {
    return value.toLocaleString();
  }

  // Months formatting
  if (unitLower === 'months') {
    return `${value} month${value !== 1 ? 's' : ''}`;
  }

  // Default: number with locale formatting
  return typeof value === 'number' ? value.toLocaleString() : String(value);
}

/**
 * Format step intermediate values (BUG-UI-030: moved from BreakdownTab)
 * Smart formatting based on metric name patterns
 * BUG-UI-046 FIX: Added 'checking', 'saving' to currency patterns
 * BUG-UI-107 FIX: Check counts BEFORE currency to avoid "Late Payments" being formatted as $1
 * BUG-UI-107 FIX: VeraScore factors are POINTS, not currency - check BEFORE currency patterns
 */
function formatStepValue(name: string, value: number, metricKey?: string): string {
  const lowerName = name.toLowerCase();
  const lowerMetricKey = metricKey?.toLowerCase() || '';

  // BUG-UI-107 FIX: VeraScore contribution/composite values are POINTS, not currency
  // These appear in the Calculation tab for verascore_current
  // Step names like "  DTI", "  Cash Flow", "  Savings", "  Liquid Reserves", "  Other Composite", "→ Other Composite"
  // These show weighted contributions (e.g., 5.55 pts) or composite scores (e.g., 49)
  const isVeraScoreStep =
    lowerMetricKey === 'verascore_current' ||
    lowerName.includes('composite') ||
    lowerName.includes('verascore') ||
    lowerName.includes('stage 1') ||
    lowerName.includes('stage 2') ||
    // VeraScore factor steps start with "  " (2 spaces) and are single words/short names
    (lowerName.startsWith('  ') && (
      lowerName.includes('dti') ||
      lowerName.includes('debt-to-income') ||
      lowerName.includes('debt management') ||
      lowerName.includes('payment history') ||
      // Only match "cash flow", "savings", "liquid reserves" when they appear as VeraScore factor names
      // (starts with "  " indicating a VeraScore step)
      lowerName.trim() === 'cash flow' ||
      lowerName.trim() === 'savings' ||
      lowerName.trim() === 'liquid reserves'
    ));

  if (isVeraScoreStep) {
    // For VeraScore: show as plain number (it's a score or weighted contribution)
    // Small values (< 100) are likely contributions, show with decimals
    // Larger values are composite/final scores, round them
    if (value < 100 && value !== Math.round(value)) {
      return `${value.toFixed(2)} pts`;
    }
    return Math.round(value).toString();
  }

  // Scores (0-100)
  if (lowerName.includes('score') || lowerName.includes('factor')) {
    return Math.round(value).toString();
  }

  // BUG-UI-107 FIX: Counts - check BEFORE currency patterns
  // "Late Payments", "On-Time Payments", "Number of X" are counts, not currency
  if (lowerName.includes('count') ||
      lowerName.includes('late payment') ||
      lowerName.includes('on-time payment') ||
      lowerName.includes('on time payment') ||
      lowerName.includes('number of')) {
    return Math.round(value).toLocaleString();
  }

  // BUG-UI-107 FIX: Age in months - check BEFORE generic currency
  if (lowerName.includes('age') && lowerName.includes('month')) {
    return `${Math.round(value)} months`;
  }

  // Percentages/ratios
  if (lowerName.includes('ratio') || lowerName.includes('%') || lowerName.includes('pct')) {
    return `${value.toFixed(1)}%`;
  }

  // Currency (income, balance, payment, account types, etc.)
  // BUG-UI-046: Added 'checking', 'saving', 'investment' for account-based metrics
  // BUG-UI-107: Exclude "late payment" patterns (handled above)
  if (lowerName.includes('income') || lowerName.includes('balance') ||
      lowerName.includes('expense') ||
      lowerName.includes('money') || lowerName.includes('sum') ||
      lowerName.includes('total') || lowerName.includes('debt') ||
      lowerName.includes('cash') || lowerName.includes('deposit') ||
      lowerName.includes('withdrawal') ||
      lowerName.includes('checking') || lowerName.includes('saving') ||
      lowerName.includes('investment') || lowerName.includes('retirement') || lowerName.includes('reserve') || // BUG-UI-130 FIX: Added 'retirement' for Retirement Net
      (lowerName.includes('payment') && !lowerName.includes('late') && !lowerName.includes('on-time') && !lowerName.includes('on time'))) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  // Default: plain number
  return value.toLocaleString();
}
